'use client';

import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { cn } from '@/lib/utils';
import { MoviePoster } from '@/types/movie-poster';
import { getTodayInTimeZone } from '@/utils/date-time-helper';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../ui/carousel';
import { MovieCarouselItemComponent } from './movie-carousel-item';

type MovieCarouselProps = {
  dataLinkCategory: string;
  items: MoviePoster[];
  nextArrowDataLinkName?: string;
  previousArrowDataLinkName?: string;
  timezone?: string;
  zipCode?: string;
};

const SCROLL_BY_SIZE = 1;
// Approximate characters fitting per line at text-sm bold for each poster width
const CHARS_PER_LINE_MOBILE = 17;  // ~150px
const CHARS_PER_LINE_DESKTOP = 29; // ~250px

export const MovieCarouselComponent = ({
  dataLinkCategory,
  items,
  nextArrowDataLinkName,
  previousArrowDataLinkName,
  timezone,
  zipCode,
}: MovieCarouselProps): React.JSX.Element | null => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState(1);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const { trackClick } = useDataAnalytics();

  const needsTwoLinesMobile = items.some(item => (item.movieTitle?.length ?? 0) > CHARS_PER_LINE_MOBILE);
  const needsTwoLinesDesktop = items.some(item => (item.movieTitle?.length ?? 0) > CHARS_PER_LINE_DESKTOP);
  const titleClassName = cn(
    needsTwoLinesMobile && 'min-h-10',
    needsTwoLinesMobile && !needsTwoLinesDesktop && 'md:min-h-fit'
  );

  const today = useMemo(() => {
    return getTodayInTimeZone(timezone);
  }, [timezone]);

  useEffect(() => {
    if (!containerRef.current || !itemRef.current) return;

    const updateVisibleItems = (): void => {
      const containerWidth = containerRef.current!.offsetWidth;
      const itemWidth = itemRef.current!.offsetWidth;
      if (itemWidth) {
        const maxVisible = Math.max(1, Math.floor(containerWidth / itemWidth));
        const visible = Math.min(maxVisible, items.length);
        setVisibleItems(visible);
      }
    };

    updateVisibleItems();

    const resizeObserver = new ResizeObserver(updateVisibleItems);
    resizeObserver.observe(containerRef.current);

    return (): void => resizeObserver.disconnect();
  }, [items?.length]);

  const displayItem = (item: MoviePoster, index: number): React.JSX.Element => {
    return (
      <MovieCarouselItemComponent dataLinkCategory={dataLinkCategory}
        date={today}
        index={index}
        movie={item}
        titleClassName={titleClassName}
        zipCode={zipCode}
      />
    );
  };

  if (!items?.length) return null;

  return (
    <div ref={containerRef}
      className="w-full"
    >
      {/* Invisible item for measurement */}
      <div className="absolute invisible pointer-events-none h-0 overflow-hidden">
        <div ref={itemRef}>
          {displayItem(items[0], 0)}
        </div>
      </div>

      <Carousel opts={{
        align: 'start',
        containScroll: 'keepSnaps',
        dragFree: true,
        skipSnaps: true,
        slidesToScroll: SCROLL_BY_SIZE,
        watchDrag: true,
        watchResize: true,
      }}
      visibleItems={visibleItems}
      setApi={(api) => {
        if (api) {
          setCarouselApi(api);
          const updateButtons = (): void => {
            const progress = api.scrollProgress();
            setCanScrollPrev(progress > 0.01);
            setCanScrollNext(progress < 0.99);
          };
          updateButtons();
          api.on('scroll', updateButtons);
          api.on('select', updateButtons);
          api.on('settle', updateButtons);
        }
      }}
      >
        <CarouselContent className="items-stretch gap-sm">
          {items.map((item, index) =>
            <CarouselItem key={item.cinemarkMovieId}>
              {displayItem(item, index)}
            </CarouselItem>
          )}
        </CarouselContent>
        {canScrollPrev &&
          <CarouselPrevious className="size-12 left-3 hidden md:flex md:top-[calc(50%-30px)]"
            dataTest="button-movie-media-carousel-previous"
            iconFontSizeClassName="size-8"
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              trackClick({
                linkCategory: dataLinkCategory,
                linkName: previousArrowDataLinkName,
              });
              carouselApi?.scrollPrev();
            }}
          />
        }
        {canScrollNext && items.length > 1 &&
          <CarouselNext className="size-12 right-1 hidden md:flex md:top-[calc(50%-30px)]"
            dataTest="button-movie-media-carousel-next"
            iconFontSizeClassName="size-8"
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              trackClick({
                linkCategory: dataLinkCategory,
                linkName: nextArrowDataLinkName,
              });
              carouselApi?.scrollNext();
            }}
          />
        }
      </Carousel>
    </div>
  );
};
